export const ADMIN_DOMAIN = 'https://admin.cuanmax.digital';
export const FORM_DOMAIN = 'https://form.cuanmax.digital';
export const LP_DOMAIN = 'https://lp.cuanmax.digital';
export const KNOWN_CUANMAX_HOSTS = ['form.cuanmax', 'lp.cuanmax', 'admin.cuanmax'];

export function brandSubdomain(brandName?: string | null): string {
  if (!brandName) return '';
  return brandName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export function brandDomain(brandName?: string | null): string {
  const slug = brandSubdomain(brandName);
  return slug ? `https://${slug}.cuanmax.digital` : FORM_DOMAIN;
}

export function isKnownCuanmaxDomain(hostname: string): boolean {
  return KNOWN_CUANMAX_HOSTS.some(h => hostname.includes(h)) || ['localhost', '127.0.0.1'].includes(hostname);
}

// Host label yang BUKAN toko brand (infra/reserved).
const RESERVED_BRAND_HOSTS = ['form', 'lp', 'admin', 'www', 'n8n', 'chat', 'api-wa', 'app'];

// Brand subdomain: {slug}.cuanmax.digital — but NOT the reserved form/lp/admin hosts.
export function isBrandSubdomain(hostname: string): boolean {
  if (!hostname.endsWith('.cuanmax.digital')) return false;
  if (isKnownCuanmaxDomain(hostname)) return false;
  const slug = hostname.replace(/\.cuanmax\.digital$/, '');
  if (!slug || RESERVED_BRAND_HOSTS.includes(slug)) return false;
  return /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(slug);
}

export function brandSubdomainFromHostname(hostname: string): string {
  return hostname.replace(/\.cuanmax\.digital$/, '');
}

export function isCuanmaxFormDomain(hostname: string): boolean {
  if (['localhost', '127.0.0.1'].includes(hostname)) return false;
    return hostname.includes('form.cuanmax') || hostname.includes('lp.cuanmax') || !hostname.includes('admin');
}

export function isCuanmaxAdminDomain(hostname: string): boolean {
  return ['localhost', '127.0.0.1'].includes(hostname) || hostname.includes('admin.cuanmax');
}

// ============================================================================
// PRODUK-FORM SUBDOMAIN — 1 level: {label}-{brand}.cuanmax.digital
// Contoh: 01-alifbata.cuanmax.digital → form order langsung (bukan katalog).
// {brand}.cuanmax.digital (tanpa awalan angka) tetap = BrandStorePage (katalog).
// PENTING: pakai STRIP (bukan titik) karena Universal SSL Free cuma cover
// level-1; skema 2-level ({label}.{brand}.cuanmax.digital) butuh Total TLS/Advanced.
// domainLabel selalu numerik → dipecah di strip pertama.
// ============================================================================

const PRODUCT_FORM_RE = /^(\d+)-([a-z0-9](?:[a-z0-9-]{0,62}))$/;

function baseLabel(hostname: string): string {
  return hostname.replace(/\.cuanmax\.digital$/, '');
}

export function isProductFormSubdomain(hostname: string): boolean {
  if (!hostname.endsWith('.cuanmax.digital')) return false;
  const m = baseLabel(hostname).match(PRODUCT_FORM_RE);
  return !!m && !RESERVED_BRAND_HOSTS.includes(m[2]);
}

export function parseProductFormSubdomain(hostname: string): { productLabel: string; brandSlug: string } | null {
  const m = baseLabel(hostname).match(PRODUCT_FORM_RE);
  if (!m || RESERVED_BRAND_HOSTS.includes(m[2])) return null;
  return { productLabel: m[1], brandSlug: m[2] };
}

// {label}-{brand}.cuanmax.digital (label = domainLabel numeric per brand)
export function productDomain(domainLabel: string | number, brandName?: string | null): string {
    const slug = brandSubdomain(brandName);
    const label = String(domainLabel ?? '').trim();
    if (!slug || !label) return FORM_DOMAIN;
    return `https://${label}-${slug}.cuanmax.digital`;
}

// ============================================================================
// BRAND CUSTOM DOMAIN — 1 domain (+www) per brand, diatur di Pengaturan Merek.
// normalizeCustomDomain: lower + buang protokol/path/port/www./titik akhir.
// apexOfCustomDomain: kupas SATU label 'www.' depan (www.toko.com → toko.com).
// ============================================================================

export function normalizeCustomDomain(host?: string | null): string {
    let h = String(host || '').trim().toLowerCase();
    h = h.replace(/^https?:\/\//, '').split('/')[0].split(':')[0];
    h = h.replace(/^www\./, '').replace(/\.+$/, '');
    return h;
}

export function apexOfCustomDomain(host?: string | null): string {
    return normalizeCustomDomain(host);
}

// Cocok bila hostname (apex ATAU www.apex) = custom_domain brand.
export function isBrandCustomDomain(hostname: string, customDomain?: string | null): boolean {
    const custom = normalizeCustomDomain(customDomain);
    if (!custom) return false;
    return normalizeCustomDomain(hostname) === custom;
}

// URL kandang toko brand: custom domain menang, fallback subdomain.
export function brandStoreUrl(brand?: { name?: string | null; custom_domain?: string | null } | null): string {
    const custom = normalizeCustomDomain(brand?.custom_domain);
    if (custom) return `https://${custom}`;
    return brandDomain(brand?.name);
}

// Model Berdu TANPA Cloudflare: customer CNAME www → apex miliknya sendiri,
// apex A → S3. Nol hostname perantara, nol token, nol dashboard CF — SSL otomatis
// via certbot S3. (Alternatif `connect.cuanmax.digital` DITANGGUHKAN 2026-10-21:
// butuh token CF valid — token di env Supabase DAN di Pengaturan Website sama-sama
// mati "Authentication error" — + butuh grey-cloud manual. Bisa dihidupkan lagi
// nanti tanpa ubah panel: cukup ganti target CNAME + flip DNS-only via backend.)
